import { Form } from "@inertiajs/react";
import { LoaderCircle } from "lucide-react";
import InputError from "@/components/input-error";
import TextLink from "@/components/text-link";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useTranslations } from "@/hooks/use-translations";
import SwitchAuthProcessLink from "@/pages/auth/switch-auth-process-link";
import login from "@/routes/login";
import password from "@/routes/password";
import { AuthProcess } from "@/types";

export default function LoginForm({
  canResetPassword,
}: {
  canResetPassword: boolean;
}) {
  const { __ } = useTranslations();

  return (
    <Form
      action={login.post()}
      className="flex flex-col gap-6"
      method="post"
      resetOnSuccess={["password"]}
    >
      {({ processing, errors }) => (
        <>
          <div className="grid gap-6">
            <div className="grid gap-2">
              <Label htmlFor="email">{__("general.email_address")}</Label>
              <Input
                autoComplete="email"
                autoFocus
                id="email"
                name="email"
                placeholder="email@example.com"
                required
                tabIndex={0}
                type="email"
              />
              <InputError message={errors.email} />
            </div>

            <div className="grid gap-2">
              <div className="flex items-center">
                <Label htmlFor="password">{__("general.password")}</Label>
                {canResetPassword && (
                  <TextLink
                    className="ml-auto text-sm"
                    href={password.request()}
                    tabIndex={0}
                  >
                    {__("auth.log_in_page.forgot_password_q")}
                  </TextLink>
                )}
              </div>
              <Input
                autoComplete="current-password"
                id="password"
                name="password"
                placeholder={__("general.password")}
                required
                tabIndex={0}
                type="password"
              />
              <InputError message={errors.password} />
            </div>

            <div className="flex items-center space-x-3">
              <Checkbox id="remember" name="remember" tabIndex={0} />
              <Label htmlFor="remember">
                {__("auth.log_in_page.remember_me")}
              </Label>
            </div>

            <Button
              className="mt-4 w-full"
              disabled={processing}
              tabIndex={0}
              type="submit"
            >
              {processing && <LoaderCircle className="h-4 w-4 animate-spin" />}
              {__("auth.log_in")}
            </Button>
          </div>

          <SwitchAuthProcessLink authProcess={AuthProcess.LogIn} />
        </>
      )}
    </Form>
  );
}
